import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { registerDestructor } from '@ember/destroyable';
import type Owner from '@ember/owner';
import Modal from 'tio-reimagined/components/modal';
import Button from 'tio-reimagined/components/button';
import { eq } from 'tio-ui/utilities';
import {
  DEMO_CLIENT_LIST,
  DEMO_PRODUCT_LIST,
  DEMO_SESSION_ROLE_LIST,
  DEMO_TA_PAYMENT_MODEL_LIST,
  DEMO_USER_SCENARIO_LIST,
  applyDemoSession,
  getCurrentDemoClientId,
  getCurrentDemoProductId,
  getCurrentDemoRoleId,
  getCurrentDemoTaPaymentModelId,
  getCurrentDemoUserScenarioId,
  getDemoData,
  getDemoProduct,
  getDemoSessionRole,
  getDemoTaPaymentModel,
  getDemoUserScenario,
  type DemoClientId,
  type DemoProductId,
  type DemoSessionRoleId,
  type DemoTaPaymentModelId,
  type DemoUserScenarioId,
} from 'tio-employee/utils/career-pathways-demo-data';

interface DemoDataShortcutSignature {
  Args: Record<string, never>;
}

export default class DemoDataShortcut extends Component<DemoDataShortcutSignature> {
  @tracked isOpen = false;
  @tracked selectedClientId: DemoClientId = getCurrentDemoClientId();
  @tracked selectedProductId: DemoProductId = getCurrentDemoProductId();
  @tracked selectedRoleId: DemoSessionRoleId = getCurrentDemoRoleId();
  @tracked selectedUserScenarioId: DemoUserScenarioId = getCurrentDemoUserScenarioId();
  @tracked selectedTaPaymentModelId: DemoTaPaymentModelId = getCurrentDemoTaPaymentModelId();

  constructor(owner: Owner, args: DemoDataShortcutSignature['Args']) {
    super(owner, args);

    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', this.handleKeyDown);
      registerDestructor(this, () => document.removeEventListener('keydown', this.handleKeyDown));
    }
  }

  get clients() {
    return DEMO_CLIENT_LIST;
  }

  get products() {
    return DEMO_PRODUCT_LIST;
  }

  get roles() {
    return DEMO_SESSION_ROLE_LIST;
  }

  get userScenarios() {
    return DEMO_USER_SCENARIO_LIST;
  }

  get taPaymentModels() {
    return DEMO_TA_PAYMENT_MODEL_LIST;
  }

  get selectedClientName() {
    return getDemoData(this.selectedClientId).client.name;
  }

  get selectedProductName() {
    return getDemoProduct(this.selectedProductId).name;
  }

  get selectedRoleName() {
    return getDemoSessionRole(this.selectedRoleId).name;
  }

  get selectedUserScenarioName() {
    return getDemoUserScenario(this.selectedUserScenarioId).name;
  }

  get selectedTaPaymentModelName() {
    return getDemoTaPaymentModel(this.selectedTaPaymentModelId).name;
  }

  private isDemoShortcut(event: KeyboardEvent) {
    return event.key.toLowerCase() === 'd' && event.shiftKey && (event.metaKey || event.ctrlKey);
  }

  private isEditableTarget(target: EventTarget | null) {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    return (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      target.isContentEditable ||
      !!target.closest('[contenteditable=""], [contenteditable="true"]')
    );
  }

  handleKeyDown = (event: KeyboardEvent) => {
    if (!this.isDemoShortcut(event) || this.isEditableTarget(event.target)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.openModal();
  };

  openModal = () => {
    this.selectedClientId = getCurrentDemoClientId();
    this.selectedProductId = getCurrentDemoProductId();
    this.selectedRoleId = getCurrentDemoRoleId();
    this.selectedUserScenarioId = getCurrentDemoUserScenarioId();
    this.selectedTaPaymentModelId = getCurrentDemoTaPaymentModelId();
    this.isOpen = true;
  };

  closeModal = () => {
    this.isOpen = false;
  };

  selectClient = (clientId: DemoClientId) => {
    this.selectedClientId = clientId;
  };

  selectProduct = (productId: DemoProductId) => {
    this.selectedProductId = productId;
  };

  selectRole = (roleId: DemoSessionRoleId) => {
    this.selectedRoleId = roleId;
  };

  selectUserScenario = (userScenarioId: DemoUserScenarioId) => {
    this.selectedUserScenarioId = userScenarioId;
  };

  selectTaPaymentModel = (taPaymentModelId: DemoTaPaymentModelId) => {
    this.selectedTaPaymentModelId = taPaymentModelId;
  };

  apply = () => {
    applyDemoSession({
      clientId: this.selectedClientId,
      productId: this.selectedProductId,
      roleId: this.selectedRoleId,
      userScenarioId: this.selectedUserScenarioId,
      taPaymentModelId: this.selectedTaPaymentModelId,
    });
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <Modal
      @isOpen={{this.isOpen}}
      @onClose={{this.closeModal}}
      @size="2xl"
      @ariaLabel="Demo data options"
      data-test-demo-data-modal
    >
      <:header>
        <div>
          <h2 class="text-lg font-semibold text-gray-900">Demo data</h2>
          <p class="mt-1 text-sm text-gray-500">
            Choose the client, product, and role to apply to this demo session.
          </p>
        </div>
      </:header>

      <:body>
        <div class="space-y-8">
          <section>
            <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-500">Client</h3>
            <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {{#each this.clients as |client|}}
                <button
                  type="button"
                  class="rounded-xl border p-4 text-left transition
                    {{if
                      (eq client.id this.selectedClientId)
                      'border-violet-500 bg-violet-50 ring-2 ring-violet-100'
                      'border-gray-200 hover:border-violet-300 hover:bg-violet-50/50'
                    }}"
                  aria-pressed={{if (eq client.id this.selectedClientId) "true" "false"}}
                  data-test-demo-client={{client.id}}
                  {{on "click" (fn this.selectClient client.id)}}
                >
                  <div class="font-semibold text-gray-900">{{client.name}}</div>
                  <div class="mt-1 text-sm text-gray-500">{{client.industry}}</div>
                  <div class="mt-2 text-xs text-gray-400">
                    {{client.employeeCount}}
                    employees &middot;
                    {{client.taUtilization}}% TA utilization
                  </div>
                </button>
              {{/each}}
            </div>
          </section>

          <section>
            <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-500">Product</h3>
            <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {{#each this.products as |product|}}
                <button
                  type="button"
                  class="rounded-xl border p-4 text-left transition
                    {{if
                      (eq product.id this.selectedProductId)
                      'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-100'
                      'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/50'
                    }}"
                  aria-pressed={{if (eq product.id this.selectedProductId) "true" "false"}}
                  data-test-demo-product={{product.id}}
                  {{on "click" (fn this.selectProduct product.id)}}
                >
                  <div class="font-semibold text-gray-900">{{product.name}}</div>
                  <div class="mt-1 text-sm text-gray-500">{{product.description}}</div>
                </button>
              {{/each}}
            </div>
          </section>

          <section>
            <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-500">Role</h3>
            <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {{#each this.roles as |role|}}
                <button
                  type="button"
                  class="rounded-xl border p-4 text-left transition
                    {{if
                      (eq role.id this.selectedRoleId)
                      'border-sky-500 bg-sky-50 ring-2 ring-sky-100'
                      'border-gray-200 hover:border-sky-300 hover:bg-sky-50/50'
                    }}"
                  aria-pressed={{if (eq role.id this.selectedRoleId) "true" "false"}}
                  data-test-demo-role={{role.id}}
                  {{on "click" (fn this.selectRole role.id)}}
                >
                  <div class="font-semibold text-gray-900">{{role.name}}</div>
                  <div class="mt-1 text-sm text-gray-500">{{role.description}}</div>
                </button>
              {{/each}}
            </div>
          </section>

          <section>
            <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-500">
              User scenario
            </h3>
            <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {{#each this.userScenarios as |scenario|}}
                <button
                  type="button"
                  class="rounded-xl border p-4 text-left transition
                    {{if
                      (eq scenario.id this.selectedUserScenarioId)
                      'border-amber-500 bg-amber-50 ring-2 ring-amber-100'
                      'border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
                    }}"
                  aria-pressed={{if (eq scenario.id this.selectedUserScenarioId) "true" "false"}}
                  data-test-demo-user-scenario={{scenario.id}}
                  {{on "click" (fn this.selectUserScenario scenario.id)}}
                >
                  <div class="font-semibold text-gray-900">{{scenario.name}}</div>
                  <div class="mt-1 text-sm text-gray-500">{{scenario.description}}</div>
                </button>
              {{/each}}
            </div>
          </section>

          <section>
            <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-500">
              TA payment model
            </h3>
            <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {{#each this.taPaymentModels as |model|}}
                <button
                  type="button"
                  class="rounded-xl border p-4 text-left transition
                    {{if
                      (eq model.id this.selectedTaPaymentModelId)
                      'border-rose-500 bg-rose-50 ring-2 ring-rose-100'
                      'border-gray-200 hover:border-rose-300 hover:bg-rose-50/50'
                    }}"
                  aria-pressed={{if (eq model.id this.selectedTaPaymentModelId) "true" "false"}}
                  data-test-demo-ta-payment-model={{model.id}}
                  {{on "click" (fn this.selectTaPaymentModel model.id)}}
                >
                  <div class="font-semibold text-gray-900">{{model.name}}</div>
                  <div class="mt-1 text-sm text-gray-500">{{model.description}}</div>
                </button>
              {{/each}}
            </div>
          </section>
        </div>
      </:body>

      <:footer>
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p class="text-sm text-gray-500">
            Applying:
            <span class="font-medium text-gray-700">{{this.selectedClientName}}</span>
            /
            <span class="font-medium text-gray-700">{{this.selectedProductName}}</span>
            /
            <span class="font-medium text-gray-700">{{this.selectedRoleName}}</span>
            /
            <span class="font-medium text-gray-700">{{this.selectedUserScenarioName}}</span>
            /
            <span class="font-medium text-gray-700">{{this.selectedTaPaymentModelName}}</span>
          </p>
          <div class="flex justify-end gap-3">
            <Button @appearance="outlined" @intent="default" {{on "click" this.closeModal}}>
              Cancel
            </Button>
            <Button @intent="primary" data-test-demo-apply {{on "click" this.apply}}>
              Apply demo data
            </Button>
          </div>
        </div>
      </:footer>
    </Modal>
  </template>
}
